import { useState, useRef, useEffect } from 'react';
import type { CompanyQualifications, QualificationItem } from '../types';
import { PreviewableImage } from './ImagePreview';
import { usePreviewTheme } from './PreviewTheme';
import { useOrgLabel } from './CommonComponents';

interface CompanyQualificationsSectionProps {
  data: CompanyQualifications;
}

export default function CompanyQualificationsSection({ data }: CompanyQualificationsSectionProps) {
  const theme = usePreviewTheme();
  const orgLabel = useOrgLabel();
  const { honors = [], businessQualifications = [], strength = [], endorsement = [] } = data || {};

  const hasContent = honors.length || businessQualifications.length || strength.length || endorsement.length;

  if (!hasContent) {
    return null;
  }

  return (
    <div>
      {/* 主标题区域 */}
      <div className="px-4 py-3 border-b border-gray-100 flex items-center">
        <div className="flex items-center gap-2">
          <h3 className="text-base font-bold" style={{ color: theme.textHighlightColor }}>{orgLabel}资质</h3>
          <p className="text-xs text-gray-400 tracking-widest ml-1">ENTERPRISE QUALIFICATIONS</p>
        </div>
      </div>

      <div className="p-4 space-y-5">
        {honors.length > 0 && <HonorsLayout items={honors} />}
        {businessQualifications.length > 0 && <BusinessLayout items={businessQualifications} />}
        {strength.length > 0 && <StrengthLayout items={strength} />}
        {endorsement.length > 0 && <EndorsementLayout items={endorsement} />}
      </div>
    </div>
  );
}

/* ============ 通用：可展开的富文本描述 ============ */
function ExpandableDescription({
  html,
  maxLines = 2,
  className = '',
  link,
}: {
  html?: string;
  maxLines?: number;
  className?: string;
  link?: string;
}) {
  const [expanded, setExpanded] = useState(false);
  const [hasOverflow, setHasOverflow] = useState(false);
  const contentRef = useRef<HTMLDivElement>(null);
  const theme = usePreviewTheme();

  useEffect(() => {
    if (!contentRef.current || !html) return;
    const el = contentRef.current;
    // 临时移除 clamp 测量完整高度
    const originalDisplay = el.style.display;
    const originalClamp = el.style.webkitLineClamp;
    const originalOrient = el.style.webkitBoxOrient;
    const originalOverflow = el.style.overflow;

    el.style.display = 'block';
    el.style.webkitLineClamp = 'none';
    el.style.webkitBoxOrient = '';
    el.style.overflow = 'visible';
    const fullHeight = el.scrollHeight;

    // 恢复 clamp
    el.style.display = originalDisplay;
    el.style.webkitLineClamp = originalClamp;
    el.style.webkitBoxOrient = originalOrient;
    el.style.overflow = originalOverflow;

    // 获取行高计算单行高度
    const styles = getComputedStyle(el);
    const lineHeight = parseFloat(styles.lineHeight) || (parseFloat(styles.fontSize) * 1.5);
    const maxHeight = lineHeight * maxLines;

    setHasOverflow(fullHeight > maxHeight + 2);
  }, [html, maxLines]);

  if (!html) return null;

  // 有 link 时总是显示跳转按钮；无 link 时仅溢出显示展开按钮
  const showMoreButton = link ? true : hasOverflow;

  const clampStyle: React.CSSProperties = !expanded
    ? {
        display: '-webkit-box',
        WebkitLineClamp: maxLines,
        WebkitBoxOrient: 'vertical' as any,
        overflow: 'hidden',
      }
    : {};

  const handleMore = () => {
    if (link) {
      // 跳转到指定页面
      if (/^https?:\/\//.test(link)) {
        window.open(link, '_blank');
      } else {
        window.location.href = link;
      }
    } else {
      setExpanded(!expanded);
    }
  };

  return (
    <div>
      <div
        ref={contentRef}
        className={`rich-text-preview ${className}`}
        style={clampStyle}
        dangerouslySetInnerHTML={{ __html: html }}
      />
      {showMoreButton && (
        <button
          onClick={handleMore}
          className="mt-1 text-[10px] flex items-center gap-0.5 transition-colors ml-auto justify-end w-fit"
          style={{ color: theme.themeColor }}
        >
          { '查看更多'}
          <span className={`inline-block transition-transform`}>
            {'→'}
          </span>
        </button>
      )}
    </div>
  );
}

/* ============ 通用：两行省略标题 ============ */
function TruncatedTitle({ text, className = '' }: { text: string; className?: string }) {
  return (
    <h5 className={`line-clamp-2 ${className}`} title={text}>
      {text}
    </h5>
  );
}

/* ============ 1. 荣誉资质 - 横向特色卡片 ============ */
function HonorsLayout({ items }: { items: QualificationItem[] }) {
  return (
    <div>
      <div className="flex items-center gap-2 mb-2.5">
        <span className="w-7 h-7 flex items-center justify-center">🏆</span>
        <h4 className="text-sm font-semibold text-gray-800">荣誉资质</h4>
        <span className="text-[10px] text-gray-400 tracking-wider">HONORS</span>
      </div>
      <div className="space-y-2">
        {items.map((item, idx) => (
          <div
            key={item.id}
            className="flex gap-3 p-3 rounded-xl hover:shadow-md transition-all duration-200"
            style={{
              background: '#f8f9fa'
            }}
          >
            <div className="relative shrink-0">
              <div className="w-20 h-20 rounded-lg overflow-hidden bg-white flex items-center justify-center shadow-sm" 
              style={{
                border: '1px solid #fde68a'
              }}>
                {item.image ? (
                  <PreviewableImage src={item.image} alt={item.title} className="w-full h-full object-contain" />
                ) : (
                  <span className="text-3xl">🏆</span>
                )}
              </div>
            </div>
            <div className="flex-1 min-w-0 flex flex-col justify-center">
              <div className="flex items-center gap-1.5 mb-1">
                <TruncatedTitle text={item.title} className="text-sm font-bold text-gray-800" />
              </div>
              <ExpandableDescription
                html={item.description}
                maxLines={2}
                link={item.link}
                className="text-[11px] text-gray-600 leading-relaxed"
              />
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

/* ============ 2. 业务资质 - 上图下文单列 ============ */
function BusinessLayout({ items }: { items: QualificationItem[] }) {
  return (
    <div>
      <div className="flex items-center gap-2 mb-2.5">
        <span className="w-7 h-7 flex items-center justify-center">📋</span>
        <h4 className="text-sm font-semibold text-gray-800">业务资质</h4>
        <span className="text-[10px] text-gray-400 tracking-wider">BUSINESS</span>
      </div>
      <div className="space-y-3">
        {items.map((item, idx) => (
          <div
            key={item.id}
            className="rounded-xl overflow-hidden hover:shadow-lg transition-all duration-200 group"
            style={{
              background: '#f8f9fa',
            }}
          >
            {/* 上图 */}
            <div className="aspect-videoflex items-center justify-center p-3 relative">
              <div className="absolute top-0 left-0 right-0 h-1.5"></div>
              {item.image ? (
                <PreviewableImage
                  src={item.image}
                  alt={item.title}
                  className="max-w-full max-h-full object-contain group-hover:scale-102 transition-transform duration-300"
                />
              ) : (
                <span className="text-5xl opacity-50 flex items-center justify-center text-white">📋</span>
              )}
            </div>
            {/* 下文 */}
            <div className="p-3">
              <div className="flex items-center gap-2 mb-1.5">
                <span className="w-1 h-4 bg-gradient-to-b from-emerald-400 to-green-600 rounded-full"></span>
                <TruncatedTitle text={item.title} className="text-sm font-semibold text-gray-800" />
              </div>
              <ExpandableDescription
                html={item.description}
                maxLines={2}
                link={item.link}
                className="text-[11px] text-gray-600 leading-relaxed"
              />
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

/* ============ 3. 实力资质 - 时间轴列表样式 ============ */
function StrengthLayout({ items }: { items: QualificationItem[] }) {
  return (
    <div>
      <div className="flex items-center gap-2 mb-2.5">
        <span className="w-7 h-7 flex items-center justify-center">💪</span>
        <h4 className="text-sm font-semibold text-gray-800">实力资质</h4>
        <span className="text-[10px] text-gray-400 tracking-wider">STRENGTH</span>
      </div>
      <div className="relative pl-4">
        <div className="absolute left-1.5 top-2 bottom-2 w-px bg-gradient-to-b from-purple-300 via-purple-200 to-transparent"></div>
        <div className="space-y-2.5">
          {items.map((item, idx) => (
            <div key={item.id} className="relative">
              <div className="absolute -left-3.5 top-3 w-3 h-3 rounded-full bg-gradient-to-br from-purple-500 to-indigo-600 ring-2 ring-white shadow-sm"></div>
              <div
                className="ml-2 p-2.5 rounded-lg hover:shadow-md transition-all duration-200"
                style={{
                  background: '#f8f9fa'
                }}
              >
                <div className="flex items-start gap-2.5">
                  <div className="shrink-0 w-14 h-14 rounded-lg overflow-hidden bg-white flex items-center justify-center border" 
                  style={{
                    border: '1px solid #704beb'
                  }}
                  >
                    {item.image ? (
                      <PreviewableImage src={item.image} alt={item.title} className="w-full h-full object-contain" />
                    ) : (
                      <span className="text-base">💪</span>
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-0.5">
                      <TruncatedTitle text={item.title} className="text-xs font-semibold text-gray-800" />
                    </div>
                    <ExpandableDescription
                      html={item.description}
                      maxLines={2}
                      link={item.link}
                      className="text-[10px] text-gray-500 leading-relaxed"
                    />
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

/* ============ 4. 合作背书 - 上图下文单列 ============ */
function EndorsementLayout({ items }: { items: QualificationItem[] }) {
  return (
    <div>
      <div className="flex items-center gap-2 mb-2.5">
        <span className="w-7 h-7 flex items-center justify-center">🏅</span>
        <h4 className="text-sm font-semibold text-gray-800">合作背书</h4>
        <span className="text-[10px] text-gray-400 tracking-wider">ENDORSEMENT</span>
      </div>
      <div className="space-y-3">
        {items.map((item, idx) => (
          <div
            key={item.id}
            className="relative rounded-xl overflow-hidden hover:shadow-lg transition-all duration-200 group"
            style={{
              background: '#f8f9fa',
            }}
          >
            {/* 上图 */}
            <div className="aspect-video flex items-center justify-center p-4 relative">
              {item.image ? (
                <img
                  src={item.image}
                  alt={item.title}
                  className="max-w-full max-h-full object-contain group-hover:scale-102 transition-transform duration-300"
                />
              ) : (
                <span className="text-5xl opacity-50">🏅</span>
              )}
            </div>

            {/* 下文 */}
            <div className="p-3 pt-2">
              <div className="flex items-center gap-2 mb-1.5">
                <span className="w-1 h-4 bg-gradient-to-b from-rose-400 to-red-500 rounded-full"></span>
                <TruncatedTitle text={item.title} className="text-sm font-bold text-gray-800" />
              </div>
              <ExpandableDescription
                html={item.description}
                maxLines={2}
                link={item.link}
                className="text-[11px] text-gray-600 leading-relaxed"
              />
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
